import Category from 'types/category';

// Helper function to get parent category name
export const getParentName = (
  category: Category,
  categories: { [key: string]: Category },
): string => {
  if (!category.parent) {
    return '';
  }
  const parentId =
    typeof category.parent === 'string' ? category.parent : category.parent.id;
  const parent = categories[parentId];
  return parent?.name || '';
};

// Helper function to get root category name
export const getRootCategoryName = (
  category: Category,
  categories: { [key: string]: Category },
): string => {
  // If rootCategory is null, this is a root category itself
  if (!category.rootCategory) {
    return category.name;
  }
  // If rootCategory is already a Category object, use it directly
  if (typeof category.rootCategory === 'object') {
    return category.rootCategory.name;
  }
  // Otherwise, rootCategory is a string ID, look it up
  const rootCategory = categories[category.rootCategory];
  return rootCategory?.name || '';
};

// Helper function to get color based on root category name
export const getRootCategoryColor = (rootCategoryName: string): string => {
  const name = rootCategoryName.toLowerCase();
  if (name === 'assets') {
    return '#0066cc'; // blue
  } else if (name === 'revenue') {
    return '#28a745'; // green
  } else if (name === 'expenses') {
    return '#dc3545'; // red
  } else if (name === 'liabilities') {
    return '#fd7e14'; // orange
  } else if (name === 'equity') {
    return '#6f42c1'; // purple
  }
  return 'inherit'; // default color
};

// Flatten categories and sort by hierarchy (children immediately after parents)
export const flattenCategories = (categories: {
  [key: string]: Category;
}): Array<{
  category: Category;
  depth: number;
  parentName: string;
  rootCategoryName: string;
}> => {
  const result: Array<{
    category: Category;
    depth: number;
    parentName: string;
    rootCategoryName: string;
  }> = [];
  const processed = new Set<string>();

  // Recursively add a category and all its children
  const addCategoryAndChildren = (category: Category, depth: number) => {
    if (processed.has(category.id)) {
      return;
    }

    const parentName = getParentName(category, categories);
    const rootCategoryName = getRootCategoryName(category, categories);
    result.push({
      category,
      depth,
      parentName,
      rootCategoryName,
    });
    processed.add(category.id);

    // Immediately add all children of this category
    // Sort children by name for consistent ordering
    if (category.children && category.children.length > 0) {
      const sortedChildren = category.children
        .map((childId) => categories[childId])
        .filter((child): child is Category => child !== undefined)
        .sort((a, b) => a.name.localeCompare(b.name));

      sortedChildren.forEach((child) => {
        addCategoryAndChildren(child, depth + 1);
      });
    }
  };

  // Start with root categories (no parent) and process each with its subtree
  // Sort root categories by name for consistent ordering
  const rootCategories = Object.values(categories)
    .filter((cat) => !cat.parent)
    .sort((a, b) => a.name.localeCompare(b.name));
  rootCategories.forEach((rootCategory) => {
    addCategoryAndChildren(rootCategory, 0);
  });

  return result;
};

/**
 * Check if a category name already exists under the same parent
 * @param categories - All categories object
 * @param name - Category name to check
 * @param parentId - Parent category ID (empty string for root categories)
 * @param excludeCategoryId - Optional category ID to exclude from check (for edit operations)
 * @returns Error message if duplicate found, null otherwise
 */
export const validateDuplicateCategoryName = (
  categories: { [key: string]: Category },
  name: string,
  parentId: string,
  excludeCategoryId?: string,
): string | null => {
  const trimmedName = name.trim().toLowerCase();

  // Check all categories for duplicates
  for (const categoryId in categories) {
    // Skip the category being edited
    if (excludeCategoryId && categoryId === excludeCategoryId) {
      continue;
    }

    const category = categories[categoryId];

    // Skip voided categories
    if (category.isVoided) {
      continue;
    }

    // Check if name matches (case-insensitive)
    if (category.name.trim().toLowerCase() === trimmedName) {
      // Get the parent ID of the existing category
      const existingParentId =
        typeof category.parent === 'string'
          ? category.parent
          : (category.parent?.id ?? '');

      // If both have the same parent (or both are root categories), it's a duplicate
      if (existingParentId === parentId) {
        const parentName =
          parentId && categories[parentId]
            ? categories[parentId].name
            : 'root level';
        return `A category with the name "${name}" already exists under ${parentName}.`;
      }

      // Check if the parent has children with the same name
      // If we're trying to create a category with the same name as a child of the parent
      if (parentId && categories[parentId]) {
        const parent = categories[parentId];
        if (parent.children && parent.children.length > 0) {
          const hasChildWithSameName = parent.children.some((childId) => {
            const child = categories[childId];
            return (
              child &&
              !child.isVoided &&
              child.name.trim().toLowerCase() === trimmedName &&
              childId !== excludeCategoryId
            );
          });

          if (hasChildWithSameName) {
            return `A category with the name "${name}" already exists as a child of "${parent.name}".`;
          }
        }
      }
    }
  }

  return null;
};
